import { Input, InputNumber, Spin, Typography } from 'antd';
import produce from 'immer';
import { get, merge } from 'lodash';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Checkbox,
  CountryInput,
  DateInput,
  DocumentInput,
  EmiratesIdInput,
  Form,
  NationalitySelect,
  PhoneNumberFormItem,
} from '~/components';
import CustomFields from '~/components/CustomFields';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import RefreshSharesButton from '~/components/RefreshSharesButton';
import features from '~/constants/features';
import { useGetDraft } from '~/controllers/draft';
import { useValidator } from '~/controllers/validation';
import { useGetApplication } from '~/controllers/wizard';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Draft } from '~/types/Draft';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import { getStagePayload } from '~/utils/getStagePayload';
import { isReadOnly } from '~/utils/isReadOnly';

interface Props {
  n: NameFn;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any;
  currentIndex?: any;
  shareholderType: 'INDIVIDUAL' | 'BUSINESSES' | 'BENEFICIAL_OWNER';
  edit?: boolean;
}

const IndividualForm: React.FC<Props> = (props) => {
  const { onFinish, initialValues, n, currentIndex, shareholderType, edit } = props;
  const { t } = useTranslation('fillApplication');
  const [form] = Form.useForm<ApplicationPayload>();
  const [loading, setLoading] = useState<boolean>(false);
  const getFormState = () => {
    return produce({ ...initialValues }, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(form.getFieldsValue());
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();
  const optionalFieldsController = useOptionalFields({ ...initialValues });
  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  const userRoles = shareholderType === 'BENEFICIAL_OWNER' ? ['beneficialOwners'] : ['individualShareholders'];

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields ?? {},
        errorFields: edit && stagePayload?.activeStep.errorInformation?.errorFields as any,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Container className="IndividualAdd">
        <Form
          form={form}
          name={formName}
          onFinish={onFinish}
          onValuesChange={(_, newValues) => {
            optionalFieldsController.debouncedUpdate(newValues);
          }}
          initialValues={initialValues}
        >
          <Form.Item
            name={n(['data', 'uid'])}
            hidden
            initialValue={get(initialValues, n(['data', 'uid'])) || createUid()}
          />
          <Form.Item
            hidden
            initialValue={get(initialValues, n(['data', 'contactId'])) || createUid()}
            name={n(['data', 'contactId'])}
          >
            <Input />
          </Form.Item>
          <Form.Item
            initialValue={get(initialValues, n(['data', 'salesForceUser']))}
            hidden
            valuePropName="checked"
            name={n(['data', 'salesForceUser'])}
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            initialValue={get(initialValues, n(['data', 'salesforceFields']))}
            hidden
            name={n(['data', 'salesforceFields'])}
          />
          <Form.Item
            initialValue={get(initialValues, n(['data', 'userRoles'])) || userRoles}
            hidden
            name={n(['data', 'userRoles'])}
          />
          <Section>
            <Typography.Title level={4}>{t('Personal details').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('First name')} name={n(['data', 'firstName'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Last name')} name={n(['data', 'lastName'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Date of birth')} name={n(['data', 'dateOfBirth'])}>
                <DateInput disableDateRule="date <= today" />
              </Form.Item>
              <Form.Item label={t('Nationality')} name={n(['data', 'nationality'])}>
                <NationalitySelect />
              </Form.Item>
            </InputsGroupLayout>
            <Form.Item
              name={n(['data', 'pep', 'isPep'])}
              valuePropName="checked"
              initialValue={get(initialValues, n(['data', 'pep', 'isPep'])) ?? false}
            >
              <Checkbox>{t('Shareholder is a Politically Exposed Person (PEP)')}</Checkbox>
            </Form.Item>
            <Form.Item
              hidden
              name={n(['data', 'pep', 'files'])}
              initialValue={get(initialValues, n(['data', 'pep', 'files'])) ?? []}
            />
            <Form.Visible name={n(['data', 'pep', 'isPep'])}>
              <Typography.Title level={4}>{t('PEP Document').toUpperCase()}</Typography.Title>
              <Form.Item
                name={n(['data', 'pep', 'files'])}
                initialValue={get(initialValues, n(['data', 'pep', 'files'])) ?? []}
              >
                <DocumentInput name="pep" variant="default" />
              </Form.Item>
            </Form.Visible>
          </Section>

          <Section>
            <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>

            <Typography.Paragraph>
              <Trans
                t={t}
                defaults="Please upload copy of <strong>passport main photo page</strong>  and complete the details in the fields below."
              />
            </Typography.Paragraph>
            <Form.Item name={n(['data', passport, 'files'])}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>

            {/* <Typography.Paragraph>
              <Trans
                t={t}
                defaults="Please upload copy of the<strong> visa page</strong>  and complete the details in the fields below."
              />
            </Typography.Paragraph>
            <Form.Item name={n(['data', 'visa', 'files'])}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item> */}

            <Typography.Title level={5}>{t('Passport details:')}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item validateFirst label={t('Document number')} name={n(['data', passport, 'documentNumber'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Passport expiry date')} name={n(['data', passport, 'expiryDate'])}>
                <DateInput disableDateRule="date > today" />
              </Form.Item>
              {/* <Form.Item label={t('Visa expiry date')} name={n(['data', 'visa', 'expiryDate'])}>
                <DateInput disableDateRule="date > today" />
              </Form.Item> */}
            </InputsGroupLayout>
          </Section>

          <Section formItemLast>
            <Typography.Title level={4}>{t('Emirates ID').toUpperCase()}</Typography.Title>

            <Typography.Paragraph>
              <Trans
                t={t}
                defaults="Please upload copy of both the<strong>  front and back </strong>of the Emirates ID."
              />
            </Typography.Paragraph>
            <Form.Item name={n(['data', emiratesId, 'files'])}>
              <DocumentInput name="photos" variant="default" maxCount={2} />
            </Form.Item>

            <Typography.Title level={5}>{t('Emirates ID details:')}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('Emirates ID number')} name={n(['data', emiratesId, 'documentNumber'])}>
                <EmiratesIdInput />
              </Form.Item>
              <Form.Item label={t('Emirates ID expiry date')} name={n(['data', emiratesId, 'expiryDate'])}>
                <DateInput disableDateRule="date > today" />
              </Form.Item>
            </InputsGroupLayout>
          </Section>

          <Section formItemLast>
            <Typography.Title level={4}>{t('UAE residency & contact details').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item
                label={t('Country')}
                name={n(['data', 'country'])}
                initialValue={features.defaultCountrySalesforce}
              >
                <CountryInput />
              </Form.Item>
              <PhoneNumberFormItem name={n(['data', 'mobileNo'])} optional />
            </InputsGroupLayout>
          </Section>

          <section>
            <CustomFields nFn={(name) => n(['data', ...[name].flat()])} />
          </section>

          <section>
            <Typography.Title level={4}>{t('Shares').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>{t('Please specify ownership share')}</Typography.Paragraph>
            <InputsGroupLayout>
              {loading ? (
                <Spin />
              ) : (
                <Form.Item label={t('Shares %')} name={n('shares')}>
                  <InputNumber className="IndividualAdd-SharesInput" max={100} />
                </Form.Item>
              )}
              <RefreshSharesButton form={form} setLoading={setLoading} shareholderType={shareholderType} n={n} />
            </InputsGroupLayout>
          </section>
        </Form>
      </Container>
    </FormItemContextProvider>
  );
};

export const formName = 'addNewIndividualShareholder';

const passport = 'passport';
const emiratesId = 'emiratesId';

const Container = styled.div`
  .IndividualAdd-SharesInput {
    width: 100%;
  }
`;

export default IndividualForm;
